import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Fuse from 'fuse.js'
import centerScreenOnCanvas from 'utils/objects/centerScreenOnCanvas'
import { useDebouncedValue } from 'utils/debounce'
import { EditorObject } from 'utils/responsiveTypes'
import Icon from 'components/Shared/Icon'
import { useCommandPalette } from './useCommandPalette'
import { useSearchableItems } from './useSearchableItems'
import { SearchResult } from './types'
import CommandPaletteResults from './CommandPaletteResults'

import './CommandPalette.scss'

interface Props {
  appId: string
}

const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.3,
  keys: ['name', 'parentName'],
}

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)
const modKey = isMac ? '⌘' : 'Ctrl'

const CommandPalette: React.FC<Props> = ({ appId }) => {
  const { isOpen, close } = useCommandPalette()
  const dispatch = useDispatch()
  const history = useHistory()
  const items = useSearchableItems(appId, isOpen)
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const debouncedQuery = useDebouncedValue(query, 150)

  const fuse = useMemo(
    () => (isOpen ? new Fuse(items, FUSE_OPTIONS) : null),
    [isOpen, items]
  )

  const filteredResults = useMemo(() => {
    if (!isOpen || !fuse) return []
    if (!debouncedQuery.trim()) return items

    return fuse.search(debouncedQuery).map(({ item }) => item)
  }, [isOpen, fuse, debouncedQuery, items])

  // Build a flat list ordered by category for keyboard navigation
  const flatResults = useMemo(() => {
    const order = ['screen', 'collection', 'component'] as const
    const sorted: SearchResult[] = []

    for (const cat of order) {
      for (const r of filteredResults) {
        if (r.category === cat) sorted.push(r)
      }
    }

    return sorted
  }, [filteredResults])

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Reset active index when the query changes; the result list reference can
  // churn on unrelated Redux updates (e.g. getScreens .filter() returning a new
  // array every render), which would otherwise constantly reset the highlight.
  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      close()

      switch (result.category) {
        case 'screen': {
          const screen = result.screenObject as EditorObject

          if (screen) {
            dispatch(centerScreenOnCanvas(screen))
          }

          break
        }
        case 'collection': {
          const dsId = result.datasourceId || ''
          const tId = result.tableId || ''

          history.push(`/apps/${appId}/data/${dsId}/${tId}`)

          break
        }
        case 'component': {
          const parentScreen = result.parentScreenObject as EditorObject

          if (parentScreen) {
            dispatch(centerScreenOnCanvas(parentScreen, result.componentId))
          }

          break
        }
        default:
          break
      }
    },
    [close, dispatch, history, appId]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex(prev => (prev < flatResults.length - 1 ? prev + 1 : 0))

          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex(prev => (prev > 0 ? prev - 1 : flatResults.length - 1))

          break
        case 'Enter':
          e.preventDefault()

          if (flatResults[activeIndex]) {
            handleSelect(flatResults[activeIndex])
          }

          break
        case 'Escape':
          e.preventDefault()
          close()

          break
        default:
          break
      }
    },
    [flatResults, activeIndex, handleSelect, close]
  )

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        close()
      }
    },
    [close]
  )

  if (!isOpen) return null

  return ReactDOM.createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="command-palette-overlay" onClick={handleOverlayClick}>
      <div className="command-palette-card">
        <div className="command-palette-input-wrapper">
          <Icon type="search" small />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search screens, collections, components..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="command-palette-shortcut">ESC</kbd>
        </div>
        <CommandPaletteResults
          results={filteredResults}
          activeIndex={activeIndex}
          onSelect={handleSelect}
          flatResults={flatResults}
        />
        <div className="command-palette-footer">
          <div className="command-palette-footer-group">
            <kbd className="command-palette-shortcut">↑</kbd>
            <kbd className="command-palette-shortcut">↓</kbd>
            <span>navigate</span>
          </div>
          <div className="command-palette-footer-group">
            <kbd className="command-palette-shortcut">↵</kbd>
            <span>select</span>
          </div>
          <div className="command-palette-footer-group command-palette-footer-group--right">
            <span>open with</span>
            <kbd className="command-palette-shortcut">{modKey}</kbd>
            <kbd className="command-palette-shortcut">K</kbd>
            <span className="command-palette-footer-divider">or</span>
            <kbd className="command-palette-shortcut">{modKey}</kbd>
            <kbd className="command-palette-shortcut">⇧</kbd>
            <kbd className="command-palette-shortcut">F</kbd>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CommandPalette
